import { uid } from 'quasar';
import { defineStore } from 'pinia';
import { authApiService } from 'src/services';
import { authSdkService } from 'src/services/user/authSdkService';
import type { dynamicObj } from 'src/pages/assets';
import { useAmountFormatStore } from './main';
import { WEB_SOCKET_URL } from 'src/pages/APIs';
import { onApiRegionChange, whenRegionReady } from 'src/pages/api-base-url';
import { isObject, isObjectEmtpy } from 'src/utils/general';
import { useAuthTokenStore, useSettingStore } from 'src/store/main';
import { getOrgId } from 'src/utils/organization/org_id_helper';
import { createSearchTraceId } from 'src/utils/searchTrace';
import {
  WebSocketConnectionState,
  WebSocketErrorType,
  WebSocketError,
  QueuedMessage,
  WebSocketEventHandler,
  WS_EVENT_HANDLER_TIMEOUT_MS,
  isRecoverableCloseCode,
} from 'src/types/websocket';
import { ExponentialBackoff } from 'src/utils/exponentialBackoff';
import {
  pushFaroMeasurement,
  pushFaroEvent,
  pushFaroError,
} from 'src/boot/faro';

// Logger utility - use console for logging
const logger = {
  error: (msg: string, ...args: any[]) => console.error(msg, ...args),
  debug: (msg: string, ...args: any[]) => console.debug(msg, ...args),
  warn: (msg: string, ...args: any[]) => console.warn(msg, ...args),
  info: (msg: string, ...args: any[]) => console.info(msg, ...args),
};

// localStorage key for offline queue persistence
const OFFLINE_QUEUE_KEY = 'docbits_ws_offline_queue';

export const useWebSocketStore = defineStore('websocket', {
  state: () => ({
    socket: {} as WebSocket,
    // Legacy boolean flags for backwards compatibility (use connectionState instead)
    isConnected: false,
    hasError: false,

    // NEW: Connection state machine
    connectionState: WebSocketConnectionState.DISCONNECTED,

    // NEW: Exponential backoff for reconnection
    backoff: new ExponentialBackoff({
      initialDelayMs: 1000,
      maxDelayMs: 60000,
      multiplier: 2,
      jitterFactor: 0.3,
      maxRetries: 10,
    }),

    // NEW: Error tracking
    lastError: null as WebSocketError | null,
    errorHistory: [] as WebSocketError[],
    maxErrorHistory: 10,

    // NEW: Connection metrics
    lastConnectedAt: null as Date | null,
    lastDisconnectedAt: null as Date | null,
    totalReconnects: 0,

    // NEW: Offline detection
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,

    // NEW: Offline message queue with localStorage persistence
    offlineMessageQueue: [] as QueuedMessage[],
    maxOfflineQueueSize: 50,

    // Existing state preserved
    pending_docs: {},
    dashboard_data: {},
    dashboard_update: {},
    tasks_data: {},
    tasks_count: 0,
    all_tasks_count: 0,
    chat: [] as dynamicObj[],
    chat_limit: 20,
    processIncrease: false,
    chat_page: 1,
    is_more_chat: true as boolean,
    proccessing: false as boolean,
    dashboard_data_flag: false,
    dashboard_update_flag: false,
    tasks_data_flag: false,
    tasks_count_data_flag: false,
    all_tasks_count_data_flag: false,
    subscribed_channels: new Map<string, boolean>(),
    pending_queue: new Map<
      string,
      {
        message: dynamicObj;
        resolve: (v: unknown) => void;
        reject: (r?: unknown) => void;
        request_id: string;
      }
    >(),
    eventHandlers: new Map<string, WebSocketEventHandler>(),
    wsClientTimings: new Map<
      string,
      {
        message_type: string;
        requested_at: string;
        requested_perf_ms: number;
        connect_wait_ms: number;
        sent_at: string;
        sent_perf_ms: number;
        send_completed_perf_ms?: number;
        request_size_bytes: number;
        response_received_at?: string;
        response_received_perf_ms?: number;
        response_size_bytes?: number;
        response_channel?: string;
        response_type?: string;
        response_success?: boolean;
        parse_ms?: number;
        support_trace_id?: string;
        search_trace_id?: string;
        raw_query?: string;
        is_fulltext?: boolean | string;
      }
    >(),
    connectPromise: null as Promise<WebSocket> | null,
    pongTimeout: 15000, // Detect dead connection within 15 seconds (was 60s - too slow)
    lastPongReceivedAt: 0,
    pingTimeoutId: null as number | null,
    reconnectTimeoutId: null as number | null,
    connectionTimeoutId: null as number | null,

    // Faro instrumentation timing
    _connectionStartTime: 0 as number,
    _lastPingSentAt: 0 as number,
    // Generation counter: incremented on each connect(). Anonymous listeners
    // check this to bail out if they belong to a stale (zombie) socket.
    _socketGeneration: 0 as number,

    // Session resume support
    sessionId: (localStorage.getItem('docbits_ws_session_id') || null) as
      | string
      | null,
    lastReceivedSeq: parseInt(
      localStorage.getItem('docbits_ws_last_seq') || '0',
      10,
    ),
    _seqPersistTimer: null as ReturnType<typeof setTimeout> | null,
    _resumeTimeoutId: null as ReturnType<typeof setTimeout> | null,
  }),

  getters: {
    // Computed getters for rich state info
    connectionInfo(state) {
      return {
        state: state.connectionState,
        isOnline: state.isOnline,
        retryAttempt: state.backoff.currentAttempt,
        maxRetries: state.backoff.maxRetries,
        lastError: state.lastError,
        lastConnectedAt: state.lastConnectedAt,
        lastDisconnectedAt: state.lastDisconnectedAt,
        pendingMessageCount: state.offlineMessageQueue.length,
      };
    },

    // User-friendly status message
    statusMessage(state): string {
      switch (state.connectionState) {
        case WebSocketConnectionState.CONNECTED:
          return 'Connected';
        case WebSocketConnectionState.CONNECTING:
          return 'Connecting...';
        case WebSocketConnectionState.AUTHENTICATING:
          return 'Authenticating...';
        case WebSocketConnectionState.RECONNECTING:
          return `Reconnecting (${state.backoff.currentAttempt + 1}/${state.backoff.maxRetries})...`;
        case WebSocketConnectionState.FAILED:
          return 'Connection failed';
        case WebSocketConnectionState.SUSPENDED:
          return 'Working offline';
        default:
          return 'Disconnected';
      }
    },
  },
  actions: {
    // ========================================
    // Faro Instrumentation Methods
    // ========================================

    /**
     * Track connection start time for latency metrics
     */
    _trackConnectionStart() {
      this._connectionStartTime = performance.now();
    },

    /**
     * Emit connection metrics on successful connect
     */
    _emitConnectionMetrics(isReconnect: boolean = false) {
      const connectTime = performance.now() - (this._connectionStartTime || 0);

      pushFaroMeasurement(
        'websocket',
        {
          connect_time_ms: Math.round(connectTime),
          reconnect_attempt: isReconnect ? 1 : 0,
        },
        {
          url: WEB_SOCKET_URL || 'unknown',
          status: 'success',
          reconnect_count: String(this.totalReconnects),
        },
      );

      logger.debug('[WebSocket] Faro: Connection metrics emitted', {
        connectTime: Math.round(connectTime),
        isReconnect,
      });
    },

    /**
     * Emit latency metrics when pong is received
     */
    _emitLatencyMetrics(roundTripMs: number) {
      pushFaroMeasurement(
        'websocket_latency',
        {
          round_trip_ms: Math.round(roundTripMs),
        },
        {
          connection_state: this.connectionState,
        },
      );
    },

    /**
     * Emit WebSocket error to Faro
     */
    _emitError(error: WebSocketError) {
      if (
        error.isRecoverable &&
        (error.type === WebSocketErrorType.NETWORK ||
          error.type === WebSocketErrorType.AUTHENTICATION)
      ) {
        pushFaroEvent('websocket_recoverable_error', {
          error_type: error.type,
          message: error.message,
          suggested_action: error.suggestedAction || 'unknown',
          reconnect_attempt: String(this.backoff.currentAttempt),
        });

        logger.debug('[WebSocket] Faro: Recoverable error event emitted', {
          type: error.type,
          message: error.message,
        });
        return;
      }

      const faroError = new Error(error.message);
      faroError.name = `WebSocket_${error.type}`;

      pushFaroError(faroError, {
        error_type: error.type,
        is_recoverable: String(error.isRecoverable),
        suggested_action: error.suggestedAction || 'unknown',
        reconnect_attempt: String(this.backoff.currentAttempt),
      });

      logger.debug('[WebSocket] Faro: Error emitted', {
        type: error.type,
        message: error.message,
      });
    },

    /**
     * Emit disconnect event to Faro
     */
    _emitDisconnect(code: number, reason: string, wasClean: boolean) {
      pushFaroEvent('websocket_disconnect', {
        close_code: String(code),
        reason: reason || 'unknown',
        was_clean: String(wasClean),
        total_reconnects: String(this.totalReconnects),
        connection_state: this.connectionState,
      });

      logger.debug('[WebSocket] Faro: Disconnect event emitted', {
        code,
        reason,
        wasClean,
      });
    },

    // ========================================
    // Connection Management
    // ========================================

    async connect() {
      // Already connected
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        return Promise.resolve(this.socket);
      }

      // Already connecting - return existing promise
      // ✅ FIX: Simplified check to prevent race condition where multiple WebSocket
      // instances are created. The previous check also required specific connectionState
      // values, but state changes can race with multiple simultaneous connect() calls,
      // causing the check to fail and create duplicate connections.
      // Reverted to simpler check that worked before commit 3dbad7d9c5.
      if (this.connectPromise) {
        return this.connectPromise;
      }

      // Check offline status
      if (!this.isOnline) {
        this.connectionState = WebSocketConnectionState.SUSPENDED;
        const p = Promise.reject(new Error('Device is offline'));
        p.catch(() => {}); // suppress unhandledrejection; matches connectPromise treatment below
        return p;
      }

      // Check if we've exceeded max retries
      if (
        !this.backoff.hasRetriesRemaining() &&
        this.connectionState === WebSocketConnectionState.RECONNECTING
      ) {
        this.connectionState = WebSocketConnectionState.FAILED;
        return Promise.reject(new Error('Max reconnection attempts exceeded'));
      }

      await whenRegionReady();

      if (!WEB_SOCKET_URL) {
        return Promise.reject(new Error('WebSocket URL not set'));
      }

      // Set state based on whether this is initial or reconnection
      this.connectionState =
        this.totalReconnects > 0
          ? WebSocketConnectionState.RECONNECTING
          : WebSocketConnectionState.CONNECTING;

      // Track connection start time for Faro metrics
      this._trackConnectionStart();

      this.connectPromise = new Promise<WebSocket>((resolve, reject) => {
        this.hasError = false;
        this.lastError = null;

        // Clean up any existing socket to prevent zombie connections
        this._cleanupSocket();

        // Increment generation so stale listeners from previous sockets bail out
        const generation = ++this._socketGeneration;

        // Enhanced WebSocket URL construction
        const protocol = WEB_SOCKET_URL.startsWith('https') ? 'wss' : 'ws';
        const url = WEB_SOCKET_URL.replace(/^https?:\/\//, '');
        const modifiedUrl = `${protocol}://${url}/ws/v2?org_id=${getOrgId()}`;

        try {
          this.socket = new WebSocket(modifiedUrl);
        } catch (err) {
          const wsError = this.categorizeError(err);
          this.handleConnectionError(wsError, reject);
          return;
        }

        // Connection timeout (15 seconds)
        this.connectionTimeoutId = window.setTimeout(() => {
          if (generation !== this._socketGeneration) return;
          if (this.socket.readyState !== WebSocket.OPEN) {
            logger.warn('[WebSocket] Connection timeout after 15s');
            // Record the error but don't schedule reconnect here.
            // socket.close() triggers onClose which handles reconnection.
            // This prevents double backoff attempts.
            const error = this.categorizeError(new Error('Connection timeout'));
            this._recordError(error);
            reject(new Error(error.message));
            this.socket.close();
          }
        }, 15000);

        this.socket.addEventListener('open', () => {
          if (generation !== this._socketGeneration) return; // stale socket
          if (this.connectionTimeoutId) {
            clearTimeout(this.connectionTimeoutId);
            this.connectionTimeoutId = null;
          }
          this.connectionState = WebSocketConnectionState.AUTHENTICATING;
          this.isConnected = true;
          this.sendToken(resolve, reject);
        });

        this.socket.addEventListener('close', this.onClose);

        this.socket.addEventListener('error', (event) => {
          if (generation !== this._socketGeneration) return; // stale socket
          if (this.connectionTimeoutId) {
            clearTimeout(this.connectionTimeoutId);
            this.connectionTimeoutId = null;
          }
          logger.warn('[WebSocket] Connection error:', {
            error: event,
            url: modifiedUrl,
            readyState: this.socket?.readyState,
            timestamp: new Date().toISOString(),
          });
          // Record error but DON'T schedule reconnect here.
          // Per WebSocket spec, 'close' always fires after 'error'.
          // onClose handles reconnection — this prevents double backoff attempts
          // that were burning 2 of 10 retries per single failure.
          const wsError = this.categorizeError(event);
          this._recordError(wsError);
          reject(new Error(wsError.message));
        });

        this.socket.addEventListener('message', (event) => {
          if (generation !== this._socketGeneration) return; // stale socket
          const receiveStartedAt = performance.now();
          try {
            const message = JSON.parse(event.data);
            const parseMs = performance.now() - receiveStartedAt;
            this.markWsResponseReceived(
              message,
              event.data,
              receiveStartedAt,
              parseMs,
            );

            if (message.type === 'ping') {
              this.handlePong();
              if (this.socket?.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ type: 'pong' }));
              }
            } else {
              this.handleMessage(message);
            }
          } catch (parseError) {
            logger.error('[WebSocket] Failed to parse message:', {
              rawData: event.data,
              error: parseError,
              timestamp: new Date().toISOString(),
            });
          }
        });
      });

      // On resubscription, also resubscribe to all of the previously subscribed channels.
      // on fresh start this loop will not be executed
      this.connectPromise
        .then(() => {
          let channels = '';
          for (const key of this.subscribed_channels.keys()) {
            channels += key + ',';
          }
          if (channels) {
            this.subscribeToChannel(channels);
          }
        })
        .catch(() => {
          // Error already handled in connect()
        });

      return this.connectPromise;
    },

    /**
     * Categorize an error for smart handling
     */
    categorizeError(error: Error | Event | any): WebSocketError {
      const timestamp = new Date();

      // Authentication errors
      if (
        error?.message?.includes('Token') ||
        error?.message?.includes('invalid_token') ||
        error?.message?.includes('401') ||
        error?.message?.includes('403')
      ) {
        return {
          type: WebSocketErrorType.AUTHENTICATION,
          message: 'Authentication failed - token may be expired',
          originalError: error,
          timestamp,
          isRecoverable: true,
          suggestedAction: 'refresh_token',
        };
      }

      // Network errors
      if (
        error?.message?.includes('WebSocket') ||
        error?.message?.includes('ECONNREFUSED') ||
        error?.message?.includes('timeout') ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('Network') ||
        error instanceof Event
      ) {
        return {
          type: WebSocketErrorType.NETWORK,
          message: 'Network connection failed',
          originalError: error,
          timestamp,
          isRecoverable: true,
          suggestedAction: 'retry',
        };
      }

      // Server errors (5xx)
      if (
        error?.message?.includes('500') ||
        error?.message?.includes('502') ||
        error?.message?.includes('503')
      ) {
        return {
          type: WebSocketErrorType.SERVER,
          message: 'Server error - service may be temporarily unavailable',
          originalError: error,
          timestamp,
          isRecoverable: true,
          suggestedAction: 'wait',
        };
      }

      // Protocol errors
      if (
        error?.message?.includes('parse') ||
        error?.message?.includes('JSON')
      ) {
        return {
          type: WebSocketErrorType.PROTOCOL,
          message: 'Protocol error - invalid message format',
          originalError: error,
          timestamp,
          isRecoverable: false,
          suggestedAction: 'give_up',
        };
      }

      // Unknown
      return {
        type: WebSocketErrorType.UNKNOWN,
        message: error?.message || 'Unknown error occurred',
        originalError: error,
        timestamp,
        isRecoverable: true,
        suggestedAction: 'retry',
      };
    },

    /**
     * Clean up an existing WebSocket before creating a new one.
     * Removes event listeners and closes the socket to prevent zombie connections.
     */
    _cleanupSocket() {
      if (this.socket && typeof this.socket.close === 'function') {
        try {
          // Remove listeners to prevent ghost events from old socket
          this.socket.removeEventListener('close', this.onClose);
          // Close if not already closed
          if (
            this.socket.readyState === WebSocket.OPEN ||
            this.socket.readyState === WebSocket.CONNECTING
          ) {
            this.socket.close(1000, 'Replacing connection');
          }
        } catch {
          // Ignore errors during cleanup
        }
      }
      // Null out reference to prevent accidental use of the dead socket
      this.socket = null as unknown as WebSocket;
    },

    /**
     * Record an error for diagnostics/Faro without scheduling reconnection.
     * Used by error handlers where onClose will handle reconnection.
     */
    _recordError(error: WebSocketError) {
      this.lastError = error;
      this.errorHistory.unshift(error);
      if (this.errorHistory.length > this.maxErrorHistory) {
        this.errorHistory.pop();
      }
      this._emitError(error);
      this.hasError = true;
      this.isConnected = false;
    },

    /**
     * Handle connection errors with smart recovery
     */
    handleConnectionError(
      error: WebSocketError,
      reject: (reason?: any) => void,
    ) {
      // Store error in history
      this.lastError = error;
      this.errorHistory.unshift(error);
      if (this.errorHistory.length > this.maxErrorHistory) {
        this.errorHistory.pop();
      }

      logger.warn('[WebSocket] Connection error:', {
        type: error.type,
        message: error.message,
        attempt: this.backoff.currentAttempt + 1,
        maxRetries: this.backoff.maxRetries,
        suggestedAction: error.suggestedAction,
        timestamp: error.timestamp.toISOString(),
      });

      // Emit error to Faro
      this._emitError(error);

      // Update legacy state
      this.hasError = true;
      this.isConnected = false;

      // Handle based on error type
      switch (error.suggestedAction) {
        case 'refresh_token':
          this.handleAuthError();
          break;

        case 'retry':
        case 'wait':
          this.scheduleReconnect();
          break;

        case 'give_up':
          this.connectionState = WebSocketConnectionState.FAILED;
          break;
      }

      // Reject the promise with proper Error object
      const rejectError = new Error(error.message);
      (rejectError as any).wsError = error;
      reject(rejectError);
    },

    /**
     * Handle authentication errors
     */
    async handleAuthError() {
      logger.warn(
        '[WebSocket] Authentication error - attempting token refresh',
      );
      const authStore = useAuthTokenStore();

      try {
        // Try to refresh the token - this method handles redirect to login on failure
        await authStore.tryToRefreshToken(false);

        // If we get here, the refresh succeeded (otherwise redirectToLogin was called)
        // Reset backoff and try to reconnect
        this.backoff.reset();
        this.scheduleReconnect(1000); // Quick retry after token refresh
      } catch (err) {
        logger.warn('[WebSocket] Token refresh failed:', err);
        this.connectionState = WebSocketConnectionState.FAILED;
      }
    },

    /**
     * Schedule a reconnection with exponential backoff
     * INCLUDES: Pre-reconnect token validation and refresh to prevent
     * repeated connection attempts with expired tokens
     */
    scheduleReconnect(customDelayMs?: number) {
      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
      }

      if (!this.backoff.hasRetriesRemaining()) {
        logger.error('[WebSocket] Max retries exceeded, giving up');
        this.connectionState = WebSocketConnectionState.FAILED;
        return;
      }

      this.backoff.recordAttempt();
      const delay = customDelayMs ?? this.backoff.getNextDelay();

      logger.info('[WebSocket] Scheduling reconnect:', {
        attempt: this.backoff.currentAttempt,
        maxRetries: this.backoff.maxRetries,
        delayMs: delay,
      });

      this.connectionState = WebSocketConnectionState.RECONNECTING;

      this.reconnectTimeoutId = window.setTimeout(async () => {
        this.connectPromise = null;
        this.reconnectTimeoutId = null;

        // ✅ FIX: Check and refresh token BEFORE attempting reconnect
        // This prevents repeated connection attempts with expired tokens
        // which would exhaust all retries without ever refreshing the token
        const authStore = useAuthTokenStore();
        if (authStore.isTokenExpired()) {
          logger.info(
            '[WebSocket] Token expired - refreshing before reconnect attempt',
          );
          try {
            const refreshed = await authStore.refreshToken();
            if (!refreshed) {
              logger.warn(
                '[WebSocket] Token refresh failed - stopping reconnect attempts',
              );
              this.connectionState = WebSocketConnectionState.FAILED;
              // Create an authentication error for proper notification
              const error: WebSocketError = {
                type: WebSocketErrorType.AUTHENTICATION,
                message: 'Session expired. Please log in again.',
                timestamp: new Date(),
                isRecoverable: false,
                suggestedAction: 'give_up',
              };
              this.lastError = error;
              return; // Don't attempt reconnect with invalid token
            }
            logger.info(
              '[WebSocket] Token refreshed successfully - proceeding with reconnect',
            );
          } catch (err) {
            logger.warn('[WebSocket] Token refresh error:', err);
            this.connectionState = WebSocketConnectionState.FAILED;
            return;
          }
        } else {
          logger.debug(
            '[WebSocket] Token still valid - proceeding with reconnect',
          );
        }

        this.connect().catch(() => {
          // Error already handled in connect()
        });
      }, delay);
    },

    /**
     * Initialize offline detection listeners
     */
    initializeOfflineDetection() {
      if (typeof window === 'undefined') return;

      let lastRegion: string | null = null;
      onApiRegionChange((region) => {
        if (lastRegion === null) {
          lastRegion = region;
          return;
        }
        if (lastRegion === region) return;
        lastRegion = region;
        logger.info('[WebSocket] Region changed, reconnecting socket', {
          region,
        });
        const wasConnected =
          this.socket && this.socket.readyState === WebSocket.OPEN;
        this._cleanupSocket();
        if (this.socket && typeof this.socket.close === 'function') {
          try {
            this.socket.close();
          } catch (closeErr) {
            logger.debug(
              '[WebSocket] socket.close() during region change threw (likely already closed)',
              closeErr,
            );
          }
        }
        this.connectionState = WebSocketConnectionState.DISCONNECTED;
        this.backoff.reset();
        if (wasConnected || this.isOnline) {
          this.connect().catch(() => {});
        }
      });

      window.addEventListener('online', () => {
        logger.info('[WebSocket] Network came online');
        this.isOnline = true;

        if (this.connectionState === WebSocketConnectionState.SUSPENDED) {
          this.backoff.reset();
          this.connect()
            .then(() => {
              // Flush offline queue after reconnection
              this.flushOfflineQueue();
            })
            .catch(() => {});
        }
      });

      window.addEventListener('offline', () => {
        logger.warn('[WebSocket] Network went offline');
        this.isOnline = false;
        this.connectionState = WebSocketConnectionState.SUSPENDED;

        // Cancel any pending reconnection
        if (this.reconnectTimeoutId) {
          clearTimeout(this.reconnectTimeoutId);
          this.reconnectTimeoutId = null;
        }
      });

      // Handle page visibility (for PWA/SPA)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          if (
            this.connectionState === WebSocketConnectionState.CONNECTED &&
            this.socket?.readyState === WebSocket.OPEN
          ) {
            // Tab is back — send immediate pong to prevent server-side timeout
            this.socket.send(JSON.stringify({ type: 'pong' }));
            this.handlePong();
          } else if (this.isOnline) {
            // Not connected — reconnect
            this.connect().catch(() => {});
          }
        }
      });
    },

    /**
     * Queue a message for sending when offline
     */
    queueOfflineMessage(
      type: string,
      data: Record<string, unknown>,
      priority: 'high' | 'normal' | 'low' = 'normal',
    ) {
      if (this.offlineMessageQueue.length >= this.maxOfflineQueueSize) {
        // Remove oldest low-priority message
        const lowPriorityIndex = this.offlineMessageQueue.findIndex(
          (m) => m.priority === 'low',
        );
        if (lowPriorityIndex >= 0) {
          this.offlineMessageQueue.splice(lowPriorityIndex, 1);
        } else {
          this.offlineMessageQueue.shift();
        }
      }

      this.offlineMessageQueue.push({
        id: uid(),
        type,
        data,
        priority,
        queuedAt: new Date(),
      });

      // Persist to localStorage
      this.persistOfflineQueue();
    },

    /**
     * Flush offline queue after reconnection
     */
    async flushOfflineQueue(): Promise<
      { id: string; success: boolean }[] | null
    > {
      if (this.offlineMessageQueue.length === 0) return null;

      logger.info(
        `[WebSocket] Flushing ${this.offlineMessageQueue.length} queued messages`,
      );

      // Sort by priority (high first)
      this.offlineMessageQueue.sort((a, b) => {
        const priorityOrder = { high: 0, normal: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });

      const results: { id: string; success: boolean }[] = [];
      while (
        this.offlineMessageQueue.length > 0 &&
        this.connectionState === WebSocketConnectionState.CONNECTED
      ) {
        const msg = this.offlineMessageQueue.shift()!;
        try {
          await this.send(msg.type, msg.data as dynamicObj);
          results.push({ id: msg.id, success: true });
        } catch (error) {
          logger.error('[WebSocket] Failed to send queued message', {
            type: msg.type,
            error,
          });
          results.push({ id: msg.id, success: false });
        }
      }

      // Clear persisted queue
      this.clearPersistedOfflineQueue();

      return results;
    },

    /**
     * Load offline queue from localStorage
     */
    loadOfflineQueue() {
      try {
        const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          // Convert date strings back to Date objects
          this.offlineMessageQueue = parsed.map(
            (msg: QueuedMessage & { queuedAt: string }) => ({
              ...msg,
              queuedAt: new Date(msg.queuedAt),
            }),
          );
          logger.info(
            `[WebSocket] Loaded ${this.offlineMessageQueue.length} queued messages from storage`,
          );
        }
      } catch (e) {
        logger.error('[WebSocket] Failed to load offline queue', e);
      }
    },

    /**
     * Persist offline queue to localStorage
     */
    persistOfflineQueue() {
      try {
        localStorage.setItem(
          OFFLINE_QUEUE_KEY,
          JSON.stringify(this.offlineMessageQueue),
        );
      } catch (e) {
        logger.warn('[WebSocket] Failed to persist offline queue', e);
      }
    },

    /**
     * Clear persisted offline queue
     */
    clearPersistedOfflineQueue() {
      try {
        localStorage.removeItem(OFFLINE_QUEUE_KEY);
      } catch (e) {
        logger.warn('[WebSocket] Failed to clear persisted offline queue', e);
      }
    },

    /**
     * Start the heartbeat system.
     * Server sends {type:'ping'} every 10s → Client responds {type:'pong'}.
     * A pongTimeout (15s) detects dead connections if no server ping arrives.
     */
    startPing() {
      this.stopPing();
      this.lastPongReceivedAt = Date.now();

      // Start dead-connection detection timer
      this.pingTimeoutId = window.setTimeout(
        () => this.checkPongTimeout(),
        this.pongTimeout,
      );
    },

    /**
     * Stop heartbeat timer. Called on close/disconnect.
     */
    stopPing() {
      if (this.pingTimeoutId) {
        clearTimeout(this.pingTimeoutId);
        this.pingTimeoutId = null;
      }
    },

    /**
     * Handle server-initiated ping. Resets dead-connection timer.
     * Called when server sends {type:'ping'}.
     */
    handlePong() {
      const now = performance.now();
      this.lastPongReceivedAt = Date.now();

      // Emit latency metrics to Faro
      if (this._lastPingSentAt > 0) {
        const roundTripMs = now - this._lastPingSentAt;
        this._emitLatencyMetrics(roundTripMs);
      }

      // Reset dead-connection timer (server is alive)
      if (this.pingTimeoutId) {
        clearTimeout(this.pingTimeoutId);
      }
      this.pingTimeoutId = window.setTimeout(
        () => this.checkPongTimeout(),
        this.pongTimeout,
      );
    },

    checkPongTimeout() {
      const timeSinceLastPong = Date.now() - this.lastPongReceivedAt;
      if (timeSinceLastPong >= this.pongTimeout) {
        logger.warn(
          '[WebSocket] No server ping received for',
          timeSinceLastPong,
          'ms - connection is dead',
        );
        // Close the dead socket after removing its listeners (via _cleanupSocket)
        // to avoid double scheduling. Reconnection is driven by scheduleReconnect() below,
        // NOT by onClose (whose listener was already removed in _cleanupSocket).
        this.stopPing();
        this._cleanupSocket();
        this.isConnected = false;
        // Track disconnection time (normally set by onClose, but we removed
        // the listener above — set here so debug state stays consistent).
        this.lastDisconnectedAt = new Date();
        this.connectPromise = null;
        this.scheduleReconnect();
      }
    },

    /**
     * Trigger reconnection. Cleans up zombie sockets (CONNECTING/CLOSING state)
     * before scheduling a new connection attempt via exponential backoff.
     */
    reconnectWebSocket() {
      // If already reconnecting, don't schedule another
      if (
        this.reconnectTimeoutId ||
        this.connectionState === WebSocketConnectionState.RECONNECTING
      ) {
        return;
      }

      // Check if socket is already open and healthy
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        return;
      }

      // Clean up zombie sockets (CONNECTING or CLOSING state)
      this._cleanupSocket();
      this.stopPing();

      // Reset connect promise so next connect() creates a fresh one
      this.connectPromise = null;

      // Use the new exponential backoff schedule
      this.scheduleReconnect();
    },

    onClose(event: CloseEvent | any) {
      logger.info('[WebSocket] Connection closed:', {
        code: event?.code,
        reason: event?.reason,
        wasClean: event?.wasClean,
        timestamp: new Date().toISOString(),
      });

      // Emit disconnect event to Faro
      this._emitDisconnect(
        event?.code || 0,
        event?.reason || '',
        event?.wasClean || false,
      );

      // Update legacy state
      this.isConnected = false;

      // Track disconnection time
      this.lastDisconnectedAt = new Date();

      // Clean up timeouts
      this.stopPing();
      if (this.connectionTimeoutId) {
        clearTimeout(this.connectionTimeoutId);
        this.connectionTimeoutId = null;
      }

      // Reset connect promise
      this.connectPromise = null;

      // Check if device went offline
      if (!this.isOnline) {
        this.connectionState = WebSocketConnectionState.SUSPENDED;
        return;
      }

      // Determine if we should reconnect based on close code
      const closeCode = event?.code || 0;
      const shouldReconnect = isRecoverableCloseCode(closeCode);

      if (shouldReconnect) {
        // ✅ FIX: Reconnect for all recoverable close codes, regardless of wasClean
        // Previously: && event?.wasClean !== true
        // Issue: Sleep/wake sends clean close with code 1012, but we should still reconnect
        logger.info(
          '[WebSocket] Recoverable close detected - scheduling reconnect:',
          {
            closeCode,
            wasClean: event?.wasClean,
            reason: event?.reason || 'No reason provided',
          },
        );

        // Categorize the error for logging
        const error = this.categorizeError(
          new Error(
            `WebSocket closed with code ${closeCode}: ${event?.reason || 'Unknown reason'}`,
          ),
        );
        this.lastError = error;

        // Schedule reconnection with backoff
        this.scheduleReconnect();
      } else if (closeCode === 1000 || closeCode === 1001) {
        // Normal closure - don't reconnect automatically
        this.connectionState = WebSocketConnectionState.DISCONNECTED;
        logger.info('[WebSocket] Normal closure, not reconnecting');
      } else {
        // Give up on unrecoverable close codes
        this.connectionState = WebSocketConnectionState.FAILED;
        logger.error('[WebSocket] Unrecoverable close, giving up:', {
          closeCode,
        });
      }
    },

    onError(event: any) {
      logger.error('[WebSocket] Error event:', {
        error: event,
        socketUrl: this.socket?.url,
        readyState: this.socket?.readyState,
        timestamp: new Date().toISOString(),
      });

      this.hasError = true;
      this.isConnected = false;
    },

    async formatValues(values: dynamicObj[]) {
      const { user_settings } = useSettingStore();
      return await useAmountFormatStore().formatMultiValues(
        values,
        'en_US',
        user_settings.AMOUNTS_LOCALE,
      );
    },

    searchValueByKey(key: string, values: dynamicObj[]) {
      return useAmountFormatStore().searchValueByKey(key, values);
    },

    async sendToken(resolve: any, reject: any) {
      try {
        // ✅ CRITICAL FIX: Use getValidatedToken() instead of authSdkService.getConfig().TOKEN
        // getValidatedToken() validates token expiration and refreshes if needed
        // This was broken after Auth SDK migration (commit bd5569ecc7)
        // The old implementation used authStore.token which may be stale/invalid
        const authStore = useAuthTokenStore();
        let token: string;

        try {
          // This returns the raw token (no "Bearer " prefix)
          // and handles expiration checks + automatic refresh
          token = await authStore.getValidatedToken();
        } catch (tokenError) {
          const errorMsg = 'Failed to get validated token for WebSocket';
          logger.warn('[WebSocket] sendToken failed:', {
            error: errorMsg,
            tokenError:
              tokenError instanceof Error
                ? tokenError.message
                : String(tokenError),
            timestamp: new Date().toISOString(),
          });
          reject(new Error(errorMsg));
          return;
        }

        if (!token) {
          const errorMsg = 'Token not available for WebSocket authentication';
          logger.warn('[WebSocket] sendToken failed:', {
            error: errorMsg,
            timestamp: new Date().toISOString(),
          });
          reject(new Error(errorMsg));
          return;
        }

        const originalToken = token;

        if (!token || token.length === 0) {
          const errorMsg = 'Token is empty or invalid';
          logger.warn('[WebSocket] sendToken failed:', {
            error: errorMsg,
            tokenLength: token?.length || 0,
            timestamp: new Date().toISOString(),
          });
          reject(new Error(errorMsg));
          return;
        }

        logger.debug('[WebSocket] Sending authentication token:', {
          tokenLength: token.length,
          tokenPrefix: token.substring(0, 10) + '...',
          timestamp: new Date().toISOString(),
        });

        this.socket.send(token);
        this.setEventHandler('auth', resolve, reject);
      } catch (authError: any) {
        logger.warn('[WebSocket] sendToken exception:', {
          error: authError.message || authError,
          stack: authError.stack,
          timestamp: new Date().toISOString(),
        });
        reject(authError);
      }
    },

    markWsResponseReceived(
      message: dynamicObj,
      rawData: unknown,
      receivedPerfMs: number,
      parseMs: number,
    ) {
      const requestId = message?.request_id;
      if (!requestId || !this.wsClientTimings.has(requestId)) return;

      const timing = this.wsClientTimings.get(requestId);
      if (!timing) return;

      timing.response_received_at = new Date().toISOString();
      timing.response_received_perf_ms = receivedPerfMs;
      timing.response_size_bytes =
        typeof rawData === 'string'
          ? rawData.length
          : (() => {
              try {
                return JSON.stringify(rawData).length;
              } catch {
                return undefined;
              }
            })();
      timing.response_channel =
        typeof message.channel === 'string' ? message.channel : undefined;
      timing.response_type =
        typeof message.type === 'string' ? message.type : undefined;
      timing.response_success =
        typeof message.success === 'boolean' ? message.success : undefined;
      timing.parse_ms = parseMs;
    },

    async sendMessageByType(
      messageType: string,
      data: dynamicObj,
    ): Promise<any> {
      const requestedAt = new Date().toISOString();
      const requestedPerfMs = performance.now();
      try {
        await this.connect();
        if (
          !this.socket ||
          this.socket.readyState !== WebSocket.OPEN ||
          this.hasError
        ) {
          this.reconnectWebSocket();
          return Promise.reject(new Error('WebSocket error'));
        }

        const request_id = this.generateRequestId();

        // For dashboard requests, ensure org_id is included
        const requestData = { ...data };
        if (messageType === 'dashboard' || messageType === 'dashboard_update') {
          const org_id = getOrgId();
          if (org_id && !requestData.org_id) {
            requestData.org_id = org_id;
          }
          if (!requestData.search_trace_id) {
            requestData.search_trace_id = createSearchTraceId();
          }
        }

        const message = {
          type: messageType,
          data: requestData,
          request_id: request_id,
        };
        const serializedMessage = JSON.stringify(message);
        const sentPerfMs = performance.now();
        const sentAt = new Date().toISOString();
        this.wsClientTimings.set(request_id, {
          message_type: messageType,
          requested_at: requestedAt,
          requested_perf_ms: requestedPerfMs,
          connect_wait_ms: Math.round(sentPerfMs - requestedPerfMs),
          sent_at: sentAt,
          sent_perf_ms: sentPerfMs,
          request_size_bytes: serializedMessage.length,
          support_trace_id: requestData.search_trace_id || request_id,
          search_trace_id: requestData.search_trace_id,
          raw_query: requestData.raw_query,
          is_fulltext: requestData.is_fulltext,
        });

        // Lazy-import the recorder so the websocket store doesn't take
        // a hard dep on debugCollector during boot. The recorder pushes
        // a synthetic api-call entry tagged transport='websocket' so
        // the perf report (shift-cmd-p) shows WS-delivered queries
        // alongside REST queries with timings + the raw_query payload.
        const finishRecording = (
          status: 'ok' | 'error' | 'timeout',
          payloadSize?: number,
        ) => {
          const completedPerfMs = performance.now();
          const completedAt = new Date().toISOString();
          const timing = this.wsClientTimings.get(request_id);
          this.wsClientTimings.delete(request_id);
          const responseReceivedPerfMs = timing?.response_received_perf_ms;
          const clientTiming = timing
            ? {
                message_type: timing.message_type,
                request_id,
                support_trace_id: timing.support_trace_id,
                search_trace_id: timing.search_trace_id,
                raw_query: timing.raw_query,
                is_fulltext: timing.is_fulltext,
                requested_at: timing.requested_at,
                sent_at: timing.sent_at,
                response_received_at: timing.response_received_at ?? null,
                completed_at: completedAt,
                connect_wait_ms: timing.connect_wait_ms,
                socket_send_ms: timing.send_completed_perf_ms
                  ? Math.round(
                      timing.send_completed_perf_ms - timing.sent_perf_ms,
                    )
                  : null,
                wait_for_response_ms: responseReceivedPerfMs
                  ? Math.round(responseReceivedPerfMs - timing.sent_perf_ms)
                  : null,
                client_after_receive_ms: responseReceivedPerfMs
                  ? Math.round(completedPerfMs - responseReceivedPerfMs)
                  : null,
                total_client_ms: Math.round(
                  completedPerfMs - timing.requested_perf_ms,
                ),
                parse_ms:
                  timing.parse_ms != null ? Math.round(timing.parse_ms) : null,
                request_size_bytes: timing.request_size_bytes,
                response_size_bytes:
                  timing.response_size_bytes ?? payloadSize ?? null,
                response_channel: timing.response_channel ?? null,
                response_type: timing.response_type ?? null,
                response_success: timing.response_success ?? null,
                socket_ready_state_at_send: WebSocket.OPEN,
              }
            : undefined;

          import('src/boot/debugCollector')
            .then(({ recordWsCall }) => {
              recordWsCall({
                type: messageType,
                request_id,
                support_trace_id:
                  timing?.support_trace_id ||
                  requestData.search_trace_id ||
                  request_id,
                search_trace_id: requestData.search_trace_id,
                duration_ms:
                  (clientTiming?.total_client_ms as number) ??
                  Math.round(completedPerfMs - requestedPerfMs),
                status,
                payload: requestData,
                response_size_bytes: timing?.response_size_bytes ?? payloadSize,
                request_size_bytes:
                  timing?.request_size_bytes ?? serializedMessage.length,
                client_timing: clientTiming,
              });
            })
            .catch(() => {
              /* recorder is best-effort; never fail the call */
            });
        };

        return new Promise((resolve, reject) => {
          if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            this.reconnectWebSocket();
            finishRecording('error');
            return reject(new Error('WebSocket_CLOSED'));
          }
          this.socket.send(serializedMessage);
          const timing = this.wsClientTimings.get(request_id);
          if (timing) {
            timing.send_completed_perf_ms = performance.now();
          }
          this.setEventHandler(
            messageType,
            (result: any) => {
              let size: number | undefined;
              try {
                size = JSON.stringify(result).length;
              } catch {
                /* ignore */
              }
              finishRecording('ok', size);
              resolve(result);
            },
            (err: any) => {
              finishRecording(
                err?.message?.includes('timeout') ? 'timeout' : 'error',
              );
              reject(err);
            },
          );
        });
      } catch (error) {
        this.reconnectWebSocket();
        return Promise.reject(error);
      }
    },

    async send(messageType: string, data: dynamicObj) {
      try {
        await this.connect();
        if (this.hasError) {
          this.reconnectWebSocket();
          return Promise.reject(new Error('WebSocket error'));
        }

        if (messageType == 'subscription') {
          const message = {
            type: messageType,
            data: data,
          };
          this.socket.send(JSON.stringify(message));
          return;
        }

        const request_id = this.generateRequestId();

        if (messageType == 'calculation') {
          data.request_id = request_id;
        }

        const message = {
          type: messageType,
          data: data,
          request_id: request_id,
        };

        return new Promise<dynamicObj>((resolve, reject) => {
          this.socket.send(JSON.stringify(message));
          this.setEventHandler(request_id, resolve, reject);
        });
      } catch (error) {
        this.reconnectWebSocket();
        return Promise.reject(error);
      }
    },

    handleMessage(data: dynamicObj) {
      // Track sequence numbers for resume support (throttle localStorage writes)
      if (typeof data.seq === 'number' && data.seq > this.lastReceivedSeq) {
        this.lastReceivedSeq = data.seq;
        if (!this._seqPersistTimer) {
          this._seqPersistTimer = setTimeout(() => {
            localStorage.setItem(
              'docbits_ws_last_seq',
              String(this.lastReceivedSeq),
            );
            this._seqPersistTimer = null;
          }, 5000);
        }
      }

      // Handle batch_response messages by unpacking nested messages
      if (data.type === 'batch_response' && data.messages) {
        logger.debug(
          '[WebSocket] Unpacking batch_response with',
          data.messages.length,
          'messages',
        );
        data.messages.forEach((message: dynamicObj) => {
          // Process each nested message individually
          this.handleMessage(message);
        });
        return;
      }

      if (data.success && data.message == 'Starting to serve client') {
        logger.info(
          '[WebSocket] Client authentication successful - ready to serve',
        );

        // Store session_id for resume support (if provided by backend)
        if (data.session_id) {
          const previousSessionId = this.sessionId;
          this.sessionId = data.session_id;
          localStorage.setItem('docbits_ws_session_id', data.session_id);

          // If we had a previous session, attempt to resume it
          if (previousSessionId && previousSessionId !== data.session_id) {
            // Validate session_id format before sending (defense-in-depth)
            const SESSION_ID_RE = /^[A-Za-z0-9_-]{32,48}$/;
            if (SESSION_ID_RE.test(previousSessionId)) {
              logger.info(
                `[WebSocket] New session assigned (${data.session_id}), attempting resume of previous session (${previousSessionId})`,
              );
              this.connectionState = WebSocketConnectionState.RESUMING;
              this.sendResumeMessage(previousSessionId);

              // Fallback: if backend doesn't respond within 5s, proceed as connected
              this._resumeTimeoutId = setTimeout(() => {
                if (
                  this.connectionState === WebSocketConnectionState.RESUMING
                ) {
                  logger.warn(
                    '[WebSocket] Resume timeout — proceeding as connected',
                  );
                  this.connectionState = WebSocketConnectionState.CONNECTED;
                }
                this._resumeTimeoutId = null;
              }, 5000);
            }
          }
        }

        if (typeof data.seq === 'number') {
          this.lastReceivedSeq = data.seq;
          localStorage.setItem('docbits_ws_last_seq', String(data.seq));
        }

        // Update state machine to CONNECTED (unless we're resuming)
        if (this.connectionState !== WebSocketConnectionState.RESUMING) {
          this.connectionState = WebSocketConnectionState.CONNECTED;
        }
        this.isConnected = true;
        this.hasError = false;

        // Track successful connection metrics
        this.lastConnectedAt = new Date();
        const isReconnect =
          this.totalReconnects > 0 || this.backoff.currentAttempt > 0;
        if (isReconnect) {
          this.totalReconnects++;
        }

        // Emit Faro connection metrics
        this._emitConnectionMetrics(isReconnect);

        // Reset backoff on successful connection
        this.backoff.reset();

        // Resolve auth promise
        this.resolveEventHandler('auth', data);

        // Send any pending queue and start ping
        this.sendPendingQueue();
        this.startPing();

        // Flush offline queue if we have messages
        if (this.offlineMessageQueue.length > 0) {
          this.flushOfflineQueue();
        }

        return;
      }

      // Handle session resume response
      if (data.message_type === 'session_resumed') {
        // Clear resume timeout
        if (this._resumeTimeoutId) {
          clearTimeout(this._resumeTimeoutId);
          this._resumeTimeoutId = null;
        }

        logger.info(
          `[WebSocket] Session resumed: ${data.session_id}, missed_events=${data.missed_events?.length || 0}`,
        );
        this.sessionId = data.session_id;
        localStorage.setItem('docbits_ws_session_id', data.session_id);
        this.lastReceivedSeq = data.last_seq || 0;
        localStorage.setItem(
          'docbits_ws_last_seq',
          String(this.lastReceivedSeq),
        );

        // Process missed events (skip session control messages to prevent loops)
        if (data.missed_events && Array.isArray(data.missed_events)) {
          for (const event of data.missed_events) {
            if (
              event.message_type === 'session_resumed' ||
              event.message_type === 'session_expired'
            )
              continue;
            this.handleMessage(event);
          }
        }

        this.connectionState = WebSocketConnectionState.CONNECTED;
        return;
      }

      // Handle session expired response
      if (data.message_type === 'session_expired') {
        if (this._resumeTimeoutId) {
          clearTimeout(this._resumeTimeoutId);
          this._resumeTimeoutId = null;
        }
        logger.warn(
          `[WebSocket] Session expired: ${data.reason}. Starting fresh.`,
        );
        this.sessionId = null;
        this.lastReceivedSeq = 0;
        localStorage.removeItem('docbits_ws_session_id');
        localStorage.removeItem('docbits_ws_last_seq');
        this.connectionState = WebSocketConnectionState.CONNECTED;
        return;
      }

      if (data.error == 'ConnectionError') {
        const error = this.categorizeError(new Error('ConnectionError'));
        this.handleConnectionError(error, () => {});
        return;
      }

      if (data.error == 'Token not valid' || data.error == 'invalid_token') {
        logger.warn('[WebSocket] Authentication failed - invalid token:', {
          error: data.error,
          tokenProvided: !!data.token,
          timestamp: new Date().toISOString(),
        });

        // Use the new auth error handler
        const error = this.categorizeError(new Error(data.error));
        this.lastError = error;
        this.hasError = true;
        // ❌ REMOVED: Don't set FAILED before attempting token refresh!
        // this.connectionState = WebSocketConnectionState.FAILED;
        // ✅ Let handleAuthError() set FAILED only if refresh actually fails (line 419)

        // Reject auth promise if pending
        this.rejectEventHandler('auth', new Error(data.error));

        // Handle auth error (will attempt token refresh and reconnect)
        this.handleAuthError();
        return;
      }

      if (data.message_key == 'subscription_confirmation') {
        logger.debug('[WebSocket] Channel subscription confirmed:', {
          channel: data.channel,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      if (data.message_key == 'unsubscription_confirmation') {
        logger.debug('[WebSocket] Channel unsubscription confirmed:', {
          channel: data.channel,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      if (data.channel == 'dashboard') {
        this.handleDashboardRequest(data);
        return;
      }

      if (data.channel == 'dashboard_update') {
        this.handleUpdateDashboardRequest(data);
        return;
      }

      if (data.channel == 'tasks') {
        this.handleTasksRequest(data);
        return;
      }

      if (data.channel == 'user_open_task_count') {
        this.handleTasksCountRequest(data);
        return;
      }

      if (data.channel == 'all_open_task_count') {
        this.handleAllOpenTasksCountRequest(data);
        return;
      }

      if (data.channel == 'get_chat_history') {
        this.processIncrease = true;
        if (this.chat_page === 1) {
          this.chat = data?.data;
        } else if (this.chat_page > 1) {
          const rs = this.chat.push(...data.data);
          if (rs) {
            (this.chat as any[]).sort((a, b) => {
              return (
                new Date(a.created_on).getTime() -
                new Date(b.created_on).getTime()
              );
            });
          }
        }

        if (data?.data.length === this.chat_limit + 1) {
          this.is_more_chat = true;
        }
        return;
      }
      if (data.channel == 'chat_supplier_detail') {
        this.chat_page = 1;
        this.getChatHistory(
          data?.data?.supplier_as_org_id,
          this.chat_limit,
          this.chat_page,
        );
      }

      if (data.channel == 'pending_docs') {
        this.handlePendingDocs(data);
        return;
      }

      // If it's not an subscription message, resolve the request promise
      if (
        !isObject(data) ||
        isObjectEmtpy(data) ||
        !data.hasOwnProperty('request_id') ||
        !data.hasOwnProperty('data')
      ) {
        return;
      }

      const request_id = data.request_id;
      if (this.eventHandlers.has(request_id)) {
        this.resolveEventHandler(request_id, data);
      }
    },
    handlePendingDocs(data: dynamicObj) {
      if (
        data.channel == 'pending_docs' &&
        !this.subscribed_channels.has(data.channel)
      ) {
        this.unsubscribeToChannel(data.channel);
      }
      this.pending_docs = data.data;
      return;
    },

    handleDashboardRequest(data: dynamicObj) {
      logger.debug('[WebSocket] Dashboard data received:', {
        channel: data.channel,
        hasData: !!data.data,
        dataKeys: data.data ? Object.keys(data.data) : [],
        documentsCount: data.data?.documents?.length || 0,
        timestamp: new Date().toISOString(),
      });

      this.dashboard_data_flag = false;
      if (this.eventHandlers.has('dashboard')) {
        logger.debug('[WebSocket] Resolving dashboard promise handler');
        this.resolveEventHandler('dashboard', data.data);
        return;
      }

      if (!data.data) {
        logger.warn('[WebSocket] Dashboard data is empty or undefined');
        return;
      }

      this.dashboard_data = data.data;
      this.dashboard_data_flag = true;
      logger.debug('[WebSocket] Dashboard data stored successfully');
    },

    handleUpdateDashboardRequest(data: dynamicObj) {
      this.dashboard_update_flag = false;
      if (this.eventHandlers.has('dashboard_update')) {
        this.resolveEventHandler('dashboard_update', data.data);
        return;
      }

      this.dashboard_update = data.data;
      this.dashboard_update_flag = true;
    },

    handleTasksRequest(data: dynamicObj) {
      this.tasks_data_flag = false;
      if (this.eventHandlers.has('tasks')) {
        this.resolveEventHandler('tasks', data.data);
        return;
      }

      this.tasks_data = data.data;
      this.tasks_data_flag = true;
    },

    handleTasksCountRequest(data: dynamicObj) {
      this.tasks_count_data_flag = false;
      this.tasks_count = 0;

      if (this.eventHandlers.has('task_open_count')) {
        this.resolveEventHandler('task_open_count', data.data);
        return;
      }
      this.tasks_count = data.data;
      this.tasks_count_data_flag = true;
    },

    handleAllOpenTasksCountRequest(data: dynamicObj) {
      this.all_tasks_count_data_flag = false;

      if (this.eventHandlers.has('all_open_task_count')) {
        this.resolveEventHandler('all_open_task_count', data.data);
        return;
      }
      this.all_tasks_count = data.data;
      this.all_tasks_count_data_flag = true;
    },

    async formatDashboardData(data: dynamicObj): Promise<dynamicObj> {
      try {
        const values: dynamicObj[] | { key: any; value: any }[] = [];
        const formatColumns = [
          'total_amount',
          'total_net_amount',
          'net_amount',
        ];

        data.data.documents.map((doc: dynamicObj) => {
          formatColumns.forEach((column: string) => {
            if (doc[column]) {
              values.push({
                key: doc[column],
                value: doc[column],
              });
            } else {
              values.push({
                key: 0,
                value: 0,
              });
            }
          });
        });

        const results = await this.formatValues(values);
        data.data.documents = data.data.documents.map((doc: dynamicObj) => {
          return {
            ...doc,
            total_amount: this.searchValueByKey(doc.total_amount, results),
            total_net_amount: this.searchValueByKey(
              doc.total_net_amount,
              results,
            ),
            net_amount: this.searchValueByKey(doc.net_amount, results),
          };
        });
        return data;
      } catch (error) {
        return {} as dynamicObj;
      }
    },
    /**
     * Send a resume message to restore a previous session after reconnect.
     */
    sendResumeMessage(previousSessionId: string) {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

      const resumeMsg = {
        type: 'resume',
        data: {
          session_id: previousSessionId,
          last_received_seq: this.lastReceivedSeq,
        },
      };
      logger.info(
        `[WebSocket] Sending resume: session=${previousSessionId} last_seq=${this.lastReceivedSeq}`,
      );
      this.socket.send(JSON.stringify(resumeMsg));
    },

    sendPendingQueue() {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

      logger.debug('[WebSocket] Sending pending queue', [
        ...this.pending_queue.keys(),
      ]);

      if (this.pending_queue.size === 0) {
        return;
      }

      this.pending_queue.forEach((data, key) => {
        const queuedData = this.pending_queue.get(key);
        if (queuedData) {
          const { message, resolve, reject, request_id } = queuedData;
          this.socket.send(JSON.stringify(message));
          this.setEventHandler(request_id, resolve, reject);
          this.pending_queue.delete(key);
        }
      });
    },

    close() {
      this.stopPing();
      if (this.reconnectTimeoutId) clearTimeout(this.reconnectTimeoutId);
      if (this.connectionTimeoutId) clearTimeout(this.connectionTimeoutId);
      if (this._resumeTimeoutId) clearTimeout(this._resumeTimeoutId);
      // Flush pending seq to localStorage before closing
      if (this._seqPersistTimer) {
        clearTimeout(this._seqPersistTimer);
        this._seqPersistTimer = null;
      }

      // Reject all pending event handlers and clear timeouts to prevent memory leaks
      this.eventHandlers.forEach((handler, key) => {
        try {
          // Clear the timeout first
          if (handler.timeoutId) {
            clearTimeout(handler.timeoutId);
          }
          handler.reject(new Error('WebSocket closed'));
        } catch (e) {
          // Ignore errors when rejecting
        }
      });
      this.eventHandlers.clear();

      // Clear other pending state
      this.pending_queue.clear();
      this.subscribed_channels.clear();
      this.wsClientTimings.clear();

      if (this.socket && typeof this.socket.close === 'function') {
        this.socket.close(1000, 'Intentional close');
      }

      // Clear session on intentional close (backend will also destroy it)
      this.sessionId = null;
      this.lastReceivedSeq = 0;
      localStorage.removeItem('docbits_ws_session_id');
      localStorage.removeItem('docbits_ws_last_seq');

      logger.debug('[WebSocket] Closed and cleaned up all pending handlers');
    },

    /**
     * Dispose method for HMR cleanup - clears all state and handlers
     */
    dispose() {
      this.close();
      this.offlineMessageQueue = [];
      this.connectPromise = null;
      logger.debug('[WebSocket] Disposed - all state cleared');
    },

    generateRequestId() {
      // Generate a unique identifier for each request
      return uid();
    },

    /**
     * Set an event handler with automatic timeout to prevent memory leaks
     * If no response is received within the timeout, the promise is rejected
     *
     * @param key - Unique identifier for the handler (messageType or request_id)
     * @param resolve - Promise resolve function
     * @param reject - Promise reject function
     * @param timeoutMs - Optional custom timeout (default: WS_EVENT_HANDLER_TIMEOUT_MS)
     */
    setEventHandler(
      key: string,
      resolve: (value: unknown) => void,
      reject: (reason?: unknown) => void,
      timeoutMs: number = WS_EVENT_HANDLER_TIMEOUT_MS,
    ) {
      // Clear any existing handler with the same key
      this.clearEventHandler(key);

      const timeoutId = setTimeout(() => {
        const handler = this.eventHandlers.get(key);
        if (handler) {
          logger.warn(`[WebSocket] Event handler timeout for key: ${key}`, {
            createdAt: handler.createdAt,
            timeoutMs,
          });
          this.eventHandlers.delete(key);
          reject(new Error(`WebSocket response timeout for ${key}`));
        }
      }, timeoutMs);

      this.eventHandlers.set(key, {
        resolve,
        reject,
        timeoutId,
        createdAt: new Date(),
      });

      logger.debug(`[WebSocket] Event handler set for key: ${key}`, {
        timeoutMs,
        totalHandlers: this.eventHandlers.size,
      });
    },

    /**
     * Clear an event handler and its associated timeout
     * Should be called when a response is received or handler is no longer needed
     *
     * @param key - Unique identifier for the handler
     * @returns The handler that was cleared, or undefined if not found
     */
    clearEventHandler(key: string): WebSocketEventHandler | undefined {
      const handler = this.eventHandlers.get(key);
      if (handler) {
        // Clear the timeout to prevent memory leaks
        if (handler.timeoutId) {
          clearTimeout(handler.timeoutId);
        }
        this.eventHandlers.delete(key);
        logger.debug(`[WebSocket] Event handler cleared for key: ${key}`, {
          totalHandlers: this.eventHandlers.size,
        });
      }
      return handler;
    },

    /**
     * Resolve an event handler's promise and clean up
     *
     * @param key - Unique identifier for the handler
     * @param value - Value to resolve the promise with
     * @returns true if handler was found and resolved, false otherwise
     */
    resolveEventHandler(key: string, value: unknown): boolean {
      const handler = this.clearEventHandler(key);
      if (handler) {
        handler.resolve(value);
        return true;
      }
      return false;
    },

    /**
     * Reject an event handler's promise and clean up
     *
     * @param key - Unique identifier for the handler
     * @param reason - Reason for rejection
     * @returns true if handler was found and rejected, false otherwise
     */
    rejectEventHandler(key: string, reason?: unknown): boolean {
      const handler = this.clearEventHandler(key);
      if (handler) {
        handler.reject(reason);
        return true;
      }
      return false;
    },

    subscribeToChannel(channels: string) {
      logger.debug('[WebSocket] Subscribing to channel(s):', {
        channels: channels,
        isConnected: this.isConnected,
        socketReadyState: this.socket?.readyState,
        timestamp: new Date().toISOString(),
      });

      // Handle comma-separated channel names
      const channelList = channels
        .split(',')
        .map((ch) => ch.trim())
        .filter((ch) => ch.length > 0);

      for (const channel of channelList) {
        logger.debug('[WebSocket] Subscribing to individual channel:', {
          channel: channel,
          timestamp: new Date().toISOString(),
        });

        this.subscribed_channels.set(channel, true);
        this.send('subscription', {
          type: 'subscribe',
          channel: channel,
        }).catch(() => {});
      }
    },
    subscribeToChat(channel: string, supplier_as_id: any) {
      this.subscribed_channels.set(channel, true);
      this.send('subscription', {
        type: 'subscribe',
        channel: channel,
        multicast_channel: supplier_as_id,
      }).catch(() => {});
    },
    async getChatHistory(
      sup_org_id: any,
      limit: number,
      page: number,
    ): Promise<void> {
      this.is_more_chat = true;
      const data = {
        type: 'get_chat_history',
        data: {
          supplier_as_org_id: sup_org_id,
          limit_chat: limit,
          offset: page,
        },
      };
      try {
        await this.connect();
        if (this.socket.readyState !== WebSocket.OPEN || this.hasError) {
          this.reconnectWebSocket();
          return Promise.reject(new Error('WebSocket error'));
        }

        return new Promise((resolve, reject) => {
          if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            this.reconnectWebSocket();
            return reject(new Error('WebSocket_CLOSED'));
          }

          this.socket.send(JSON.stringify(data));
        });
      } catch (error) {
        this.reconnectWebSocket();
        return Promise.reject(error);
      } finally {
        this.is_more_chat = false;
      }
    },
    async sendChat(data: any): Promise<any> {
      try {
        await this.connect();
        if (this.socket.readyState !== WebSocket.OPEN || this.hasError) {
          this.reconnectWebSocket();
          return Promise.reject(new Error('WebSocket error'));
        }

        return new Promise((resolve, reject) => {
          if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            this.reconnectWebSocket();
            return reject(new Error('WebSocket_CLOSED'));
          }

          this.socket.send(JSON.stringify(data));
        });
      } catch (error) {
        this.reconnectWebSocket();
        return Promise.reject(error);
      } finally {
        this.proccessing = false;
      }
    },
    unsubscribeToChannel(channels: string) {
      logger.debug('[WebSocket] Unsubscribing from channel(s):', {
        channels: channels,
        timestamp: new Date().toISOString(),
      });

      // Handle comma-separated channel names
      const channelList = channels
        .split(',')
        .map((ch) => ch.trim())
        .filter((ch) => ch.length > 0);

      for (const channel of channelList) {
        logger.debug('[WebSocket] Unsubscribing from individual channel:', {
          channel: channel,
          timestamp: new Date().toISOString(),
        });

        this.subscribed_channels.delete(channel);
        this.send('subscription', {
          type: 'unsubscribe',
          channel: channel,
        }).catch(() => {});
      }
    },
    reSetChat() {
      this.chat = [];
    },
  },
});
